/**
 * The market value from real sold sales, for raw or PSA 10.
 *
 * Reading a card's sales off PriceCharting takes a few seconds the first time, so the
 * card says it is reading rather than sitting blank. Values are in Singapore dollars with
 * the rate and its date, and US dollars beside them, as the brief asks.
 */

import { useEffect, useState } from "react";
import { ChevronDown, ExternalLink } from "lucide-react";
import { type Rate, type Sale, type Sold, salesApi, type ValuedGrade } from "./api";
import { day, sgd, sgDay, shortDay, usd } from "./format";
import { Callout, cx, Segmented, Skeleton } from "./ui";

interface Props {
  cardId: number;
  /** Chosen here and shared with the evaluation below, so both are for the same grade. */
  grade: ValuedGrade;
  onGrade: (grade: ValuedGrade) => void;
  onError: (e: unknown) => void;
}

const GRADES = [
  { value: "ungraded", label: "Raw" },
  { value: "psa10", label: "PSA 10" },
] as const;

export function MarketValue({ cardId, grade, onGrade, onError }: Props) {
  const [data, setData] = useState<{ sold: Sold; rate: Rate | null } | null>(null);
  const [failed, setFailed] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);

  useEffect(() => {
    let live = true;
    setData(null);
    setFailed(false);
    setShowExcluded(false);
    salesApi
      .sales(cardId, grade)
      .then((r) => live && setData(r))
      .catch((e) => {
        if (!live) return;
        onError(e);
        setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [cardId, grade, onError]);

  const rate = data?.rate?.rate ?? null;
  const sold = data?.sold;

  return (
    <section className="card p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="label">Market value</div>
          <div className="mt-0.5 text-xs text-slate-500">From real sold sales</div>
        </div>
        <Segmented value={grade} options={GRADES} onChange={onGrade} />
      </div>

      {failed && <Callout kind="error">The sold sales could not be loaded. Try again in a moment.</Callout>}

      {!failed && !sold && (
        <div>
          <Skeleton className="h-9 w-40" />
          <Skeleton className="mt-2 h-4 w-56" />
          <Skeleton className="mt-5 h-2 w-full rounded-full" />
          <p className="mt-4 text-xs text-slate-500">Reading recent sales. The first time for a card can take up to half a minute.</p>
        </div>
      )}

      {sold && (
        <div className="animate-fadein">
          {sold.estimate != null ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="num text-[34px] font-semibold leading-none tracking-tight text-slate-900">{sgd(sold.estimate, rate)}</span>
                <span className="num text-sm text-slate-400">{usd(sold.estimate)}</span>
              </div>
              <RangeBar low={sold.low} mid={sold.estimate} high={sold.high} rate={rate} />
              <p className="mt-3 text-xs text-slate-500">
                Middle of the {sold.used.length} newest matching sale{sold.used.length === 1 ? "" : "s"}
                {data?.rate ? (
                  <> · US$1 = S$<span className="num">{data.rate.rate}</span> ({day(data.rate.day)}, ECB)</>
                ) : (
                  " · no exchange rate yet"
                )}
              </p>
            </>
          ) : (
            <div className="text-[15px] font-medium text-slate-700">No market value</div>
          )}

          {(sold.flags.length > 0 || sold.readFailed) && (
            <div className="mt-4 space-y-2">
              {sold.readFailed && (
                <Callout kind="error">
                  New sales could not be read just now: {sold.readFailed}
                  {sold.readAt && ` Showing sales read ${sgDay(sold.readAt)}.`}
                </Callout>
              )}
              {sold.flags.map((f) => (
                <Callout key={f.kind} kind={f.kind === "mismatched" ? "info" : "warn"}>{f.message}</Callout>
              ))}
            </div>
          )}

          {sold.used.length > 0 && (
            <div className="mt-4">
              <div className="label mb-1">Sales used</div>
              <ul className="divide-y divide-slate-100">
                {sold.used.map((s) => (
                  <SaleRow key={s.saleId} sale={s} rate={rate} />
                ))}
              </ul>
            </div>
          )}

          {sold.excluded.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                className="flex items-center gap-1 text-[13px] font-medium text-navy-700"
                onClick={() => setShowExcluded(!showExcluded)}
              >
                <ChevronDown className={cx("h-4 w-4 transition", showExcluded && "rotate-180")} />
                {sold.excluded.length} sale{sold.excluded.length === 1 ? "" : "s"} left out
              </button>
              {showExcluded && (
                <ul className="mt-1 divide-y divide-slate-100 animate-fadein">
                  {sold.excluded.map(({ sale, reason }) => (
                    <SaleRow key={sale.saleId} sale={sale} rate={rate} reason={reason} />
                  ))}
                </ul>
              )}
            </div>
          )}

          <p className="mt-4 border-t border-slate-100 pt-3 text-[11px] leading-relaxed text-slate-400">
            Sold prices only, never asking prices.{sold.readAt && ` Sales read ${sgDay(sold.readAt)} from PriceCharting (eBay sales).`}
          </p>
        </div>
      )}
    </section>
  );
}

/** Lowest to highest of the sales used, with the middle marked. */
function RangeBar({ low, mid, high, rate }: { low: number | null; mid: number; high: number | null; rate: string | null }) {
  if (low == null || high == null || high <= low) return null;
  const at = ((mid - low) / (high - low)) * 100;
  return (
    <div className="mt-4">
      <div className="relative h-2 rounded-full bg-gradient-to-r from-navy-100 via-navy-200 to-navy-100">
        <div className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white bg-navy-900 shadow" style={{ left: `${at}%` }} />
      </div>
      <div className="num mt-1.5 flex justify-between text-[11px] text-slate-500">
        <span>Low {sgd(low, rate)}</span>
        <span>High {sgd(high, rate)}</span>
      </div>
    </div>
  );
}

function SaleRow({ sale, rate, reason }: { sale: Sale; rate: string | null; reason?: string }) {
  return (
    <li className={cx("flex gap-3 py-2.5", reason && "opacity-75")}>
      <div className="num w-12 flex-none pt-0.5 text-xs font-medium text-slate-500">{shortDay(sale.soldOn)}</div>
      <div className="min-w-0 flex-1">
        <div className="line-clamp-2 text-[13px] leading-snug text-slate-700">{sale.title ?? "(no title)"}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400">
          {/^ebay$/i.test(sale.marketplace) && /^\d+$/.test(sale.saleId) ? (
            <a
              href={`https://www.ebay.com/itm/${sale.saleId}`}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-0.5 text-navy-600 hover:underline"
            >
              {sale.marketplace}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span>{sale.marketplace}</span>
          )}
          {sale.askedCents != null && <span>offer accepted · asked {usd(sale.askedCents)}</span>}
          {reason && <span className="font-medium text-amber-700">left out: {reason}</span>}
        </div>
      </div>
      <div className="flex-none text-right">
        <div className={cx("num text-[14px] font-semibold", reason ? "text-slate-500 line-through decoration-slate-300" : "text-slate-900")}>{sgd(sale.priceCents, rate)}</div>
        <div className="num text-[11px] text-slate-400">{usd(sale.priceCents)}</div>
      </div>
    </li>
  );
}
